import _extends from "@babel/runtime/helpers/extends";
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { Field, RequiredAsterisk } from '@atlaskit/form';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { N300, N500 } from '@atlaskit/theme/colors';
import { messages } from '../messages';
import { IntlProviderWithResolvedMessages } from './IntlProviderWithResolvedMessages';
const FeedbackForm = ({
  showTypeField = true,
  showDefaultTextFields = true,
  customContent,
  onClose,
  onSubmit,
  feedbackTitle,
  feedbackTitleDetails,
  feedbackGroupLabels,
  summaryPlaceholder,
  canBeContactedLabel,
  enrolInResearchLabel,
  submitButtonLabel,
  cancelButtonLabel,
  anonymousFeedback,
  hasDescriptionDefaultValue,
  selectLabel,
  customTextAreaLabel
}) => {
  const [canBeContacted, setCanBeContacted] = useState(false);
  const [description, setDescription] = useState('');
  const [enrollInResearchGroup, setEnrollInResearchGroup] = useState(false);
  const [type, setType] = useState('empty');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    formatMessage
  } = useIntl();
  const isTypeSelected = () => type !== 'empty';
  const canShowTextField = isTypeSelected() || !showTypeField;
  const hasDescription = description || hasDescriptionDefaultValue;
  const isDisabled = showTypeField ? !isTypeSelected() || !hasDescription : !hasDescription;
  const getFieldLabels = record => ({
    bug: (record === null || record === void 0 ? void 0 : record.bug.fieldLabel) || formatMessage(messages.formBugLabel),
    comment: (record === null || record === void 0 ? void 0 : record.comment.fieldLabel) || formatMessage(messages.formCommentLabel),
    suggestion: (record === null || record === void 0 ? void 0 : record.suggestion.fieldLabel) || formatMessage(messages.formSuggestionLabel),
    question: (record === null || record === void 0 ? void 0 : record.question.fieldLabel) || formatMessage(messages.formQuestionLabel),
    empty: (record === null || record === void 0 ? void 0 : record.empty.fieldLabel) || formatMessage(messages.formEmptyLabel)
  });
  const getSelectOptions = record => [{
    label: (record === null || record === void 0 ? void 0 : record.question.selectOptionLabel) || formatMessage(messages.selectionOptionQuestionLabel),
    value: 'question'
  }, {
    label: (record === null || record === void 0 ? void 0 : record.comment.selectOptionLabel) || formatMessage(messages.selectionOptionCommentLabel),
    value: 'comment'
  }, {
    label: (record === null || record === void 0 ? void 0 : record.bug.selectOptionLabel) || formatMessage(messages.selectionOptionBugLabel),
    value: 'bug'
  }, {
    label: (record === null || record === void 0 ? void 0 : record.suggestion.selectOptionLabel) || formatMessage(messages.selectionOptionSuggestionLabel),
    value: 'suggestion'
  }];
  const getDefaultPlaceholder = record => (record === null || record === void 0 ? void 0 : record.empty.selectOptionLabel) || formatMessage(messages.selectionOptionDefaultPlaceholder);
  const focusRef = useRef();
  return /*#__PURE__*/React.createElement(Modal, {
    shouldCloseOnOverlayClick: false,
    shouldCloseOnEscapePress: false,
    autoFocus: focusRef,
    onClose: onClose,
    testId: "feedbackCollectorModalDialog"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: async () => {
      setIsSubmitting(true);
      await onSubmit({
        canBeContacted,
        description,
        enrollInResearchGroup,
        type
      });
      setIsSubmitting(false);
    }
  }, ({
    formProps
  }) => /*#__PURE__*/React.createElement("form", formProps, /*#__PURE__*/React.createElement(ModalHeader, null, /*#__PURE__*/React.createElement(ModalTitle, null, feedbackTitle || /*#__PURE__*/React.createElement(FormattedMessage, messages.feedbackTitle)), /*#__PURE__*/React.createElement(Button, {
    style: {
      lineHeight: 'normal'
    },
    spacing: 'none',
    onClick: onClose,
    appearance: 'subtle'
  }, /*#__PURE__*/React.createElement(EditorCloseIcon, {
    label: "Close Modal",
    primaryColor: `var(--ds-text-subtle, ${N500})`
  }))), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement("p", {
    style: {
      color: `var(--ds-text-subtle, ${N300})`,
      marginBottom: "var(--ds-space-300, 24px)"
    }
  }, formatMessage(messages.requiredFieldsSummary), /*#__PURE__*/React.createElement(RequiredAsterisk, null)), feedbackTitleDetails, customContent, showTypeField ? /*#__PURE__*/React.createElement(Field, {
    name: "topic",
    label: selectLabel || formatMessage(messages.selectionOptionDefaultLabel),
    isRequired: true,
    "aria-required": true // JCA11Y-1619
  }, ({
    fieldProps: {
      id,
      ...restProps
    }
  }) => /*#__PURE__*/React.createElement(Select, _extends({}, restProps, {
    onChange: option => {
      if (!option || option instanceof Array) {
        return;
      }
      setType(option.value);
    },
    menuPortalTarget: document.body,
    styles: {
      menuPortal: base => ({
        ...base,
        zIndex: 9999
      })
    },
    options: getSelectOptions(feedbackGroupLabels)
    // @ts-ignore
    ,
    ref: focusRef,
    placeholder: getDefaultPlaceholder(feedbackGroupLabels),
    inputId: id
  }))) : null, showDefaultTextFields && canShowTextField && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
    label: showTypeField ? getFieldLabels(feedbackGroupLabels)[type] : customTextAreaLabel || formatMessage(messages.defaultCustomTextAreaLabel),
    isRequired: true,
    name: "description"
  }, ({
    fieldProps
  }) => /*#__PURE__*/React.createElement(TextArea, _extends({}, fieldProps, {
    name: "foo",
    minimumRows: 6,
    placeholder: summaryPlaceholder || undefined,
    onChange: e => setDescription(e.target.value),
    value: description
  }))), !anonymousFeedback && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
    name: "can-be-contacted"
  }, ({
    fieldProps
  }) => /*#__PURE__*/React.createElement(Checkbox, _extends({}, fieldProps, {
    "aria-describedby": undefined // JCA11Y-1988
    ,
    label: canBeContactedLabel || /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.canBeContactedLabel, {
      values: {
        a: chunks => /*#__PURE__*/React.createElement("a", {
          href: "https://www.atlassian.com/legal/privacy-policy",
          target: "_blank"
        }, chunks)
      }
    })),
    onChange: event => setCanBeContacted(event.target.checked)
  }))), /*#__PURE__*/React.createElement(Field, {
    name: "enroll-in-research-group"
  }, ({
    fieldProps
  }) => /*#__PURE__*/React.createElement(Checkbox, _extends({}, fieldProps, {
    "aria-describedby": undefined // JCA11Y-1988
    ,
    label: enrolInResearchLabel || formatMessage(messages.enrolInResearchLabel),
    onChange: event => setEnrollInResearchGroup(event.target.checked)
  })))) || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
    name: 'anonymousFeedback'
  }, () => /*#__PURE__*/React.createElement(SectionMessage, {
    title: formatMessage(messages.feedbackIsAnonymousTitle),
    appearance: 'information'
  }, /*#__PURE__*/React.createElement("p", null, formatMessage(messages.feedbackIsAnonymous))))))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    onClick: onClose
  }, cancelButtonLabel || /*#__PURE__*/React.createElement(FormattedMessage, messages.cancelButtonLabel)), /*#__PURE__*/React.createElement(Button, {
    appearance: "primary",
    type: "submit",
    isDisabled: isSubmitting || isDisabled,
    testId: "feedbackCollectorSubmitBtn"
  }, submitButtonLabel || /*#__PURE__*/React.createElement(FormattedMessage, messages.submitButtonLabel))))));
};
const FeedbackFormWithIntl = ({
  locale,
  ...props
}) => {
  return /*#__PURE__*/React.createElement(IntlProviderWithResolvedMessages, {
    locale: locale
  }, /*#__PURE__*/React.createElement(FeedbackForm, props));
};
export default FeedbackFormWithIntl;